import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { fmtDate } from './FormBits.jsx';
import MicButton from './MicButton.jsx';
import { useDraft } from '../lib/draft.js';

// Session 20: per-post approval in the quarterly plan (client and agency).
// Every post has its own Approve and Replace, saved straight away through
// portal_plan_post_review. Replace opens a note box directly under that post.
// The bottom button (portal_plan_submit) sends only the Replace posts back,
// or approves the plan once every post is approved. Approved posts never
// change again; posts regenerated in the latest round show "New" and keep the
// previous version under "See the previous version".
//
// props
//   plan       a v_portal_plan_jobs row (posts, post_reviews, post_history, review_round)
//   stage      'client' | 'agency': whose decisions count (the reviewer now)
//   readOnly   show the decisions, no buttons (e.g. the agency watching a sent plan)
//   approveLabel  the bottom button when every post is approved
//   renderTitle(post, i)  optional: the agency's inline title editor
//   blocked    optional text: why the bottom button can't be used yet (e.g. unsaved title edits)
//   onSubmitted(result, {replacements, approved})   after a successful submit
//   hint       optional lede line above the posts

// The decision that counts for `stage`. A client's view row has no by_role
// (the view already masked agency decisions to undecided).
export function effective(entry, stage) {
  if (!entry) return { state: 'undecided' };
  if (!('by_role' in entry)) return { state: entry.state || 'undecided', note: entry.note || '' };
  if (entry.by_role === 'client') {
    if (stage === 'client') return { state: entry.state || 'undecided', note: entry.note || '' };
    if (entry.state === 'approved') return { state: 'approved', locked: true };
    return { state: 'undecided' };
  }
  if (entry.by_role === 'agency' && stage === 'agency') return { state: entry.state || 'undecided', note: entry.note || '' };
  return { state: 'undecided' };
}

export function tally(posts, reviews, stage) {
  const s = { approved: 0, replace: 0, undecided: 0 };
  for (const p of posts) s[effective(reviews[p.slug], stage).state] += 1;
  return s;
}

function NoteBox({ planId, slug, saved, disabled, onSaved }) {
  const [text, setText] = useState(saved || '');
  const [state, setState] = useState(null);   // null | 'saving' | 'saved' | error text
  const lastSaved = useRef(saved || '');
  const ref = useRef(null);
  const clearDraft = useDraft(`plan.post-note.${planId}.${slug}`, text, setText);

  async function save() {
    const t = text.trim();
    if (t === lastSaved.current.trim()) return;
    if (t.length > 2000) { setState('The note is too long (max 2000 characters).'); return; }
    setState('saving');
    const { data, error } = await supabase.rpc('portal_plan_post_review', { p_plan_id: planId, p_slug: slug, p_action: 'replace', p_note: t || null });
    if (error) { setState(error.message); return; }
    lastSaved.current = t;
    clearDraft();
    setState('saved');
    onSaved(data);
  }

  return (
    <div className="post-note" onClick={(e) => e.stopPropagation()}>
      <label className="field">
        <span className="field-label">What would you like instead? (optional)</span>
        <textarea ref={ref} className="review-textarea" rows={3} value={text} maxLength={2000} disabled={disabled}
                  onChange={(e) => { setText(e.target.value); setState(null); }} onBlur={save}
                  placeholder="A different topic, an angle, something to avoid…" />
      </label>
      <div className="post-note-actions">
        <MicButton value={text} onChange={setText} inputRef={ref} disabled={disabled} />
        <button type="button" className="btn" disabled={disabled || state === 'saving'} onClick={save}>
          {state === 'saving' ? 'Saving…' : 'Save note'}
        </button>
        {state === 'saved' && <span className="hint post-note-state">Saved</span>}
        {state && state !== 'saving' && state !== 'saved' && <span className="post-note-error">{state}</span>}
      </div>
    </div>
  );
}

function PreviousVersion({ versions }) {
  const [open, setOpen] = useState(false);
  if (!versions || versions.length === 0) return null;
  const prev = versions[versions.length - 1];
  return (
    <div className="post-prev">
      <button type="button" className="link-btn" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> See the previous version
      </button>
      {open && (
        <div className="post-prev-body">
          <div className="plan-card-title">{prev.title}</div>
          <div className="plan-card-meta">
            {prev.primary_keyword && <span><strong>Search</strong> {prev.primary_keyword}</span>}
            {prev.post_type && <span className="plan-card-type">{String(prev.post_type).toLowerCase()}</span>}
          </div>
          {(prev.client_summary || prev.why_now) && <p className="plan-card-why">{prev.client_summary || prev.why_now}</p>}
        </div>
      )}
    </div>
  );
}

export default function PostReview({ plan, stage, readOnly, approveLabel, renderTitle, blocked, onSubmitted, hint, clientId }) {
  const posts = plan.posts || [];
  const [reviews, setReviews] = useState(plan.post_reviews || {});
  const [busy, setBusy] = useState(null);          // slug being saved, or 'submit'
  const [rowError, setRowError] = useState({});
  const [overall, setOverall] = useState('');
  const [banner, setBanner] = useState(null);
  const overallRef = useRef(null);
  const clearOverall = useDraft(readOnly ? null : `plan.overall.${plan.job_id}`, overall, setOverall);
  const round = plan.review_round || 0;
  const history = plan.post_history || {};

  useEffect(() => { setReviews(plan.post_reviews || {}); }, [plan.job_id, plan.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  // The email link lands on #post-<slug> (the first New post).
  useEffect(() => {
    const id = decodeURIComponent((window.location.hash || '').slice(1));
    if (!id.startsWith('post-')) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [plan.job_id]);

  const t = tally(posts, reviews, stage);
  const isNew = (slug) => round > 0 && reviews[slug]?.round === round;

  async function act(slug, action) {
    setBusy(slug); setRowError((e) => ({ ...e, [slug]: null })); setBanner(null);
    const { data, error } = await supabase.rpc('portal_plan_post_review', { p_plan_id: plan.job_id, p_slug: slug, p_action: action });
    setBusy(null);
    if (error) { setRowError((e) => ({ ...e, [slug]: error.message })); return; }
    store(slug, data);
  }
  function store(slug, entry) {
    setReviews((r) => ({ ...r, [slug]: { ...(r[slug] || {}), ...entry } }));
  }

  async function submit() {
    setBusy('submit'); setBanner(null);
    const { data, error } = await supabase.rpc('portal_plan_submit', { p_plan_id: plan.job_id, p_overall_note: overall.trim() || null });
    setBusy(null);
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    clearOverall();
    onSubmitted?.(data, { replacements: t.replace, approved: t.replace === 0 });
  }

  let button = null;
  if (t.replace > 0) button = { label: `Send ${t.replace} replacement${t.replace === 1 ? '' : 's'}`, ok: true };
  else if (t.undecided === 0 && posts.length > 0) button = { label: approveLabel || 'Approve plan', ok: true };
  else button = { label: approveLabel || 'Approve plan', ok: false, why: `Approve or replace every post first (${t.undecided} left)` };

  return (
    <div className="post-review">
      {hint && <p className="hint" style={{ marginTop: 0 }}>{hint}</p>}
      <p className="post-review-summary" aria-live="polite">
        <strong>{t.approved}</strong> approved · <strong>{t.replace}</strong> to replace · <strong>{t.undecided}</strong> to decide
      </p>
      <ol className="plan-cards" aria-label="Posts in this plan">
        {posts.map((p, i) => {
          const e = effective(reviews[p.slug], stage);
          const saving = busy === p.slug;
          return (
            <li key={p.slug || i} id={`post-${p.slug}`} className={`plan-card post-card ${e.state === 'approved' ? 'plan-card-approve' : e.state === 'replace' ? 'plan-card-replace' : ''}`}>
              <div className="plan-card-main">
                <div className="plan-card-title">
                  {renderTitle ? renderTitle(p, i) : p.title}
                  {isNew(p.slug) && <span className="mode-tag new-tag">New</span>}
                </div>
                <div className="plan-card-meta">
                  <span><strong>Publishes</strong> {fmtDate(p.publish_date)}</span>
                  {p.primary_keyword && <span><strong>Search</strong> {p.primary_keyword}</span>}
                  {p.money_page && <span><strong>Supports</strong> {p.money_page}</span>}
                  {p.post_type && <span className="plan-card-type">{String(p.post_type).toLowerCase()}</span>}
                </div>
                {(p.why || p.client_summary) && <p className="plan-card-why">{p.why || p.client_summary}</p>}
                <PreviousVersion versions={history[p.slug]} />
                {e.state === 'approved' && (
                  <p className="post-state post-state-ok">
                    {e.locked ? 'Approved by the client ✓' : 'Approved ✓'}
                    {!readOnly && !e.locked && <> · <button type="button" className="link-btn" disabled={saving} onClick={() => act(p.slug, 'undo')}>Undo</button></>}
                  </p>
                )}
                {e.state === 'replace' && (
                  <p className="post-state post-state-replace">
                    Replacement requested
                    {!readOnly && <> · <button type="button" className="link-btn" disabled={saving} onClick={() => act(p.slug, 'undo')}>Undo</button></>}
                  </p>
                )}
                {e.state === 'replace' && readOnly && e.note && <p className="hint">Note: {e.note}</p>}
                {rowError[p.slug] && <p className="post-note-error">{rowError[p.slug]}</p>}
              </div>
              {!readOnly && !e.locked && (
                <div className="plan-card-actions" role="group" aria-label={`Decision for post ${i + 1}`}>
                  <button type="button" disabled={saving || busy === 'submit'}
                          className={`mark-btn ${e.state === 'approved' ? 'on-approve' : ''}`} aria-pressed={e.state === 'approved'}
                          onClick={() => act(p.slug, e.state === 'approved' ? 'undo' : 'approve')}>
                    {e.state === 'approved' ? '✓ ' : ''}Approve
                  </button>
                  <button type="button" disabled={saving || busy === 'submit'}
                          className={`mark-btn ${e.state === 'replace' ? 'on-change' : ''}`} aria-pressed={e.state === 'replace'}
                          onClick={() => act(p.slug, e.state === 'replace' ? 'undo' : 'replace')}>
                    {e.state === 'replace' ? '✓ ' : ''}Replace
                  </button>
                </div>
              )}
              {!readOnly && e.state === 'replace' && (
                <NoteBox planId={plan.job_id} slug={p.slug} saved={e.note} disabled={busy === 'submit'}
                         onSaved={(entry) => store(p.slug, entry)} />
              )}
            </li>
          );
        })}
      </ol>

      {!readOnly && (
        <section className="review-section post-review-bottom">
          <label className="field">
            <span className="field-label">Overall comments (optional)</span>
            <textarea ref={overallRef} className="review-textarea" rows={3} value={overall} onChange={(e) => setOverall(e.target.value)}
                      placeholder="Anything about the plan as a whole. It goes along with every replacement." />
          </label>
          <MicButton value={overall} onChange={setOverall} inputRef={overallRef} disabled={busy === 'submit'} clientId={clientId} hint />
          {banner && <div className={`banner banner-${banner.tone}`} style={{ marginTop: 12 }}>{banner.text}</div>}
          <div className="review-actions">
            {(!button.ok || blocked) && <span className="hint" style={{ margin: 0 }}>{blocked || button.why}</span>}
            <button type="button" className="btn btn-primary" disabled={!button.ok || !!blocked || busy !== null} onClick={submit}>
              {busy === 'submit' ? 'Sending…' : button.label}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
